/*
 * ====================================================================================
 * FILE: AnalysisPage.jsx
 * PURPOSE: Fleet economics — the seven dashboards that answer "is the iron paying?".
 *
 * DASHBOARDS (switched by the KPI strip, one API call each, loaded on demand):
 *   utilization  — rental utilization (is it earning?) + operating utilization (is it working?)
 *   idle         — days on rent where the machine never ran
 *   latency      — days between a de-hire and the next rental, and what is waiting now
 *   maintenance  — PM plan adherence + PM tickets closed by their due date
 *   conversion   — rental pipeline: quoted → won / lost
 *   revenue      — revenue per asset (accrued from contract rates, billed from invoices)
 *   customers    — revenue per customer against the service burden they create
 *
 * Replaces the old Overview + Dashboard pages. Every panel states its own source
 * and shows loading / empty / error states rather than an empty axis frame.
 * ====================================================================================
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Typography, Card, CardContent, Avatar, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Skeleton, Button, Alert, Chip,
    ToggleButton, ToggleButtonGroup, LinearProgress, Tooltip, Divider,
} from '@mui/material';
import {
    Speed as UtilizationIcon,
    HourglassEmpty as IdleIcon,
    EventBusy as LatencyIcon,
    BuildCircle as MaintenanceIcon,
    TrendingUp as ConversionIcon,
    CurrencyRupee as RevenueIcon,
    Groups as CustomersIcon,
    Refresh as RefreshIcon,
    GetApp as DownloadIcon,
    InfoOutlined as InfoIcon,
    SearchOff as EmptyIcon,
    QueryStats as MarketIcon,
    OpenInNew as OpenInNewIcon,
    AccountBalance as FinanceIcon,
    WarningAmber as FlagIcon,
} from '@mui/icons-material';
import {
    Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement,
    ArcElement, Title, Tooltip as ChartTooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import analysisService from '../../services/analysisService';
import { statusLabel } from '../../utils/statusLabels';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement,
    ArcElement, Title, ChartTooltip, Legend, Filler);

/* Chart colours mirror the theme's semantic tokens (theme.js §palette) — Chart.js
   needs literal strings, so they live here in one place instead of scattered. */
const C = {
    primary: '#2563eb', success: '#10b981', warning: '#f59e0b', error: '#ef4444',
    info: '#0ea5e9', violet: '#7c3aed', teal: '#0f766e', slate: '#94a3b8',
    grid: 'rgba(148,163,184,0.22)',
};

const VIEWS = [
    { key: 'finance',     label: 'Finance Overview',      icon: <FinanceIcon />,     color: C.success },
    { key: 'utilization', label: 'Utilization',           icon: <UtilizationIcon />, color: C.primary },
    { key: 'idle',        label: 'Idle Time',             icon: <IdleIcon />,        color: C.warning },
    { key: 'latency',     label: 'Off-rent Latency',      icon: <LatencyIcon />,     color: C.violet },
    { key: 'maintenance', label: 'Maintenance Compliance',icon: <MaintenanceIcon />, color: C.teal },
    { key: 'conversion',  label: 'Quote → Order',         icon: <ConversionIcon />,  color: C.info },
    { key: 'revenue',     label: 'Revenue per Asset',     icon: <RevenueIcon />,     color: C.success },
    { key: 'customers',   label: 'Customer Profitability',icon: <CustomersIcon />,   color: C.error },
    { key: 'market',      label: 'Demand & Pricing',      icon: <MarketIcon />,      color: C.violet },
];

const FETCHERS = {
    utilization: analysisService.getUtilization,
    idle: analysisService.getIdleTime,
    latency: analysisService.getOffRentLatency,
    maintenance: analysisService.getMaintenanceCompliance,
    conversion: analysisService.getQuoteConversion,
    revenue: analysisService.getRevenuePerAsset,
    customers: analysisService.getCustomerProfitability,
    market: analysisService.getMarketDemand,
    finance: analysisService.getFinanceOverview,
};

// --- formatting helpers ----------------------------------------------------
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const nf = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN'));
const pctText = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}%`);
const inr = (n) => (n == null ? '—' : '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 }));
const inrShort = (n) => {
    if (n == null) return '—';
    const v = Number(n);
    if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
    if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(2)} L`;
    return inr(v);
};
const monthLabel = (m) => {
    if (!m) return '—';
    const [y, mo] = m.split('-');
    return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(mo) - 1]} ${String(y).slice(2)}`;
};
const dayLabel = (d) => (d ? d.slice(8, 10) + '/' + d.slice(5, 7) : '—');
const plural = (n, word) => `${nf(n)} ${word}${Number(n) === 1 ? '' : 's'}`;

const csvEscape = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const downloadCsv = (filename, headers, rows) => {
    const csv = [headers.join(','), ...rows.map(r => r.map(csvEscape).join(','))].join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.setAttribute('download', filename);
    document.body.appendChild(a); a.click(); a.parentNode.removeChild(a);
    URL.revokeObjectURL(url);
};

// --- shared presentation pieces --------------------------------------------
/** One headline number. `hint` explains how it is derived (every metric here is derived). */
const Kpi = ({ label, value, hint, color = C.primary, loading }) => (
    <Card sx={{ flex: '1 1 190px', minWidth: 180, borderRadius: '14px' }}>
        <CardContent sx={{ p: '14px 16px !important' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.6875rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                    {label}
                </Typography>
                {hint && (
                    <Tooltip title={hint}>
                        <InfoIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                    </Tooltip>
                )}
            </Stack>
            <Typography sx={{ fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.6rem', lineHeight: 1.25, color, mt: 0.5 }}>
                {loading ? <Skeleton width={80} /> : (value ?? '—')}
            </Typography>
        </CardContent>
    </Card>
);

/** A titled panel — chart or table — with its own subtitle/source line. */
const Panel = ({ title, subtitle, action, children, sx }) => (
    <Card sx={{ borderRadius: '16px', ...sx }}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1.5 }}>
                <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem' }}>{title}</Typography>
                    {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
                </Box>
                {action}
            </Stack>
            {children}
        </CardContent>
    </Card>
);

const EmptyState = ({ message = 'No data for this period', hint = 'Try a wider date range.' }) => (
    <Box sx={{ py: 5, textAlign: 'center' }}>
        <EmptyIcon sx={{ fontSize: 42, color: 'text.disabled' }} />
        <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ mt: 1 }}>{message}</Typography>
        <Typography variant="caption" color="text.disabled">{hint}</Typography>
    </Box>
);

const ChartBox = ({ height = 280, children }) => <Box sx={{ height, position: 'relative' }}>{children}</Box>;

/** Dense table with the app's header treatment; rows come pre-formatted. */
const DataTable = ({ columns, rows, empty = 'Nothing to show', maxHeight = 420 }) => {
    if (!rows.length) return <EmptyState message={empty} hint="" />;
    return (
        <TableContainer sx={{ maxHeight }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {columns.map((c) => (
                            <TableCell key={c.key} align={c.align || 'left'} sx={{ whiteSpace: 'nowrap' }}>{c.label}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r, i) => (
                        <TableRow key={r.__key || i} hover>
                            {columns.map((c) => (
                                <TableCell key={c.key} align={c.align || 'left'} sx={c.sx}>{c.render ? c.render(r) : (r[c.key] ?? '—')}</TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

/** Inline % bar — colour always rides with the number (never colour alone). */
const PctBar = ({ value, color = C.primary }) => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 120 }}>
        <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, Number(value) || 0))}
            sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'rgba(148,163,184,.18)', '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 } }}
        />
        <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 44, textAlign: 'right' }}>{pctText(value)}</Typography>
    </Stack>
);

const machineCell = (r) => (
    <Box>
        <Typography variant="body2" fontWeight={700}>{r.serial_number || r.equipment_id || '—'}</Typography>
        <Typography variant="caption" color="text.disabled">{[r.brand, r.model].filter(Boolean).join(' · ') || r.equipment_id}</Typography>
    </Box>
);

const baseChartOptions = (yTitle, extra = {}) => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    datasets: { bar: { maxBarThickness: 56 } },
    plugins: {
        legend: { position: 'top', align: 'end', labels: { usePointStyle: true, boxWidth: 8, font: { weight: '600' } } },
        tooltip: { enabled: true },
        ...(extra.plugins || {}),
    },
    scales: {
        x: { grid: { display: false }, ticks: { font: { size: 11 } } },
        y: { beginAtZero: true, grid: { color: C.grid }, title: { display: !!yTitle, text: yTitle, font: { size: 11, weight: '600' } } },
        ...(extra.scales || {}),
    },
});

// ===========================================================================
const AnalysisPage = () => {
    const [view, setView] = useState('finance');
    const today = useMemo(() => isoDay(new Date()), []);
    const [from, setFrom] = useState(() => isoDay(new Date(Date.now() - 89 * 86400000)));
    const [to, setTo] = useState(() => isoDay(new Date()));
    const [data, setData] = useState({});        // view → payload
    const [loading, setLoading] = useState({});  // view → bool
    const [errors, setErrors] = useState({});    // view → message

    const load = useCallback(async (key, force = false) => {
        if (!force && data[key]) return;
        setLoading(l => ({ ...l, [key]: true }));
        setErrors(e => ({ ...e, [key]: null }));
        try {
            const res = await FETCHERS[key](from, to);
            if (res && res.success) setData(d => ({ ...d, [key]: res }));
            else throw new Error(res?.message || 'Request failed');
        } catch (err) {
            setErrors(e => ({ ...e, [key]: err.message || 'Failed to load' }));
        } finally {
            setLoading(l => ({ ...l, [key]: false }));
        }
    }, [from, to, data]);

    // Reload the open dashboard when the window changes; drop the rest of the cache.
    useEffect(() => {
        setData({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to]);

    useEffect(() => {
        if (!data[view] && !loading[view]) load(view);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, from, to, data]);

    const applyPreset = (dayCount) => {
        setTo(today);
        setFrom(isoDay(new Date(Date.now() - (dayCount - 1) * 86400000)));
    };

    const d = data[view];
    const isLoading = !!loading[view];
    const error = errors[view];

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }} className="animate-fade-in">
            {/* Date range — no page title, the top bar already names the page */}
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
                    <ToggleButtonGroup exclusive size="small" value={null}
                        sx={{ height: '38px', '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 1.5, borderRadius: '10px !important', borderColor: 'rgba(148,163,184,.4)' } }}>
                        <ToggleButton value="30" onClick={() => applyPreset(30)}>30D</ToggleButton>
                        <ToggleButton value="90" onClick={() => applyPreset(90)}>90D</ToggleButton>
                        <ToggleButton value="365" onClick={() => applyPreset(365)}>1Y</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField size="small" type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }} sx={{ width: 168 }}
                        InputProps={{ sx: { borderRadius: '12px', height: '38px', bgcolor: 'background.paper' } }} />
                    <TextField size="small" type="date" label="To" value={to} onChange={(e) => setTo(e.target.value)}
                        InputLabelProps={{ shrink: true }} sx={{ width: 168 }}
                        InputProps={{ sx: { borderRadius: '12px', height: '38px', bgcolor: 'background.paper' } }} />
                    <Button variant="outlined" color="inherit" startIcon={<RefreshIcon />} onClick={() => load(view, true)}
                        sx={{ borderColor: 'rgba(148,163,184,.4)', color: 'text.secondary', fontWeight: 600, height: '38px', textTransform: 'none', borderRadius: '10px' }}>
                        Refresh
                    </Button>
                </Stack>
            </Stack>

            {/* Dashboard switcher */}
            <Box sx={{ display: 'flex', gap: 1.25, overflowX: 'auto', pb: 1, mb: 2.25,
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(148,163,184,.3)', borderRadius: 3 } }}>
                {VIEWS.map((v) => {
                    const active = view === v.key;
                    return (
                        <Card key={v.key} onClick={() => setView(v.key)}
                            sx={{
                                flex: '0 0 auto', minWidth: 176, cursor: 'pointer', borderRadius: '14px',
                                border: active ? `1.5px solid ${v.color}` : '1px solid rgba(148,163,184,0.16)',
                                boxShadow: active ? `0 4px 14px ${v.color}30` : undefined,
                                bgcolor: active ? `${v.color}0d` : 'background.paper',
                                transition: 'border-color .18s ease, box-shadow .18s ease, background-color .18s ease',
                                '&:hover': { borderColor: v.color },
                            }}>
                            <CardContent sx={{ p: '12px 14px !important', display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                <Avatar variant="rounded" sx={{ bgcolor: `${v.color}1a`, color: v.color, width: 30, height: 30, borderRadius: '9px' }}>
                                    {React.cloneElement(v.icon, { sx: { fontSize: 18 } })}
                                </Avatar>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', lineHeight: 1.2 }}>{v.label}</Typography>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}
                    action={<Button size="small" onClick={() => load(view, true)}>Retry</Button>}>
                    Couldn’t load this dashboard: {error}
                </Alert>
            )}

            {view === 'utilization' && <UtilizationView d={d} loading={isLoading} range={{ from, to }} />}
            {view === 'idle' && <IdleView d={d} loading={isLoading} range={{ from, to }} />}
            {view === 'latency' && <LatencyView d={d} loading={isLoading} range={{ from, to }} />}
            {view === 'maintenance' && <MaintenanceView d={d} loading={isLoading} range={{ from, to }} />}
            {view === 'conversion' && <ConversionView d={d} loading={isLoading} range={{ from, to }} />}
            {view === 'revenue' && <RevenueView d={d} loading={isLoading} range={{ from, to }} />}
            {view === 'customers' && <CustomersView d={d} loading={isLoading} range={{ from, to }} />}
            {view === 'market' && <MarketView d={d} loading={isLoading} />}
            {view === 'finance' && <FinanceView d={d} loading={isLoading} range={{ from, to }} />}
        </Box>
    );
};


/* ── Demand & Pricing ────────────────────────────────────────────────────────
   Three questions on one page: what did demand actually do (24 months of the
   rental book), what is it likely to do next (statistical forecast with a
   confidence band and its own back-tested accuracy), and what should that mean
   for the rate card (utilization-banded suggestions — never applied
   automatically). The MEWP market headlines the Fleet Agent collects live here
   too: they are stored in the database and read here, not mailed out. */
const MarketView = ({ d, loading }) => {
    const demandChart = useMemo(() => {
        if (!d?.history?.length) return null;
        const hist = d.history;
        const fc = d.forecast?.points || [];
        const labels = [...hist.map(h => monthLabel(h.month)), ...fc.map(f => monthLabel(f.month))];
        const pad = new Array(hist.length).fill(null);
        // The forecast line starts where history ends so the two connect.
        const lastActual = hist.length ? hist[hist.length - 1].avg_machines_earning : null;
        return {
            labels,
            datasets: [
                { type: 'bar', label: 'Machines earning (actual)', order: 3, borderRadius: 6,
                  backgroundColor: C.primary,
                  data: [...hist.map(h => h.avg_machines_earning), ...fc.map(() => null)] },
                { type: 'line', label: 'Forecast', order: 1, borderColor: C.violet, backgroundColor: C.violet,
                  borderDash: [6, 4], tension: .3, pointRadius: 3,
                  data: [...pad.slice(0, -1), lastActual, ...fc.map(f => f.avg_machines_earning)] },
                { type: 'line', label: 'Forecast range', order: 2, borderColor: 'transparent',
                  backgroundColor: `${C.violet}22`, fill: '+1', pointRadius: 0,
                  data: [...pad, ...fc.map(f => f.high)] },
                { type: 'line', label: '', order: 2, borderColor: 'transparent', pointRadius: 0,
                  backgroundColor: 'transparent',
                  data: [...pad, ...fc.map(f => f.low)] },
            ],
        };
    }, [d]);

    const intelChart = useMemo(() => {
        const weeks = d?.intel?.weekly || [];
        if (!weeks.length) return null;
        return {
            labels: weeks.map(w => w.week.replace(/^\d{4}-/, '')),
            datasets: [{ label: 'MEWP market items collected', data: weeks.map(w => w.items),
                backgroundColor: C.teal, borderRadius: 6 }],
        };
    }, [d]);

    if (loading && !d) return <LoadingPanels />;
    if (!d) return null;

    const next = d.forecast?.points?.[0] || null;
    const latest = d.history?.filter(h => h.complete).slice(-1)[0] || null;
    const items = d.intel?.items || [];

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Kpi label="Fleet" value={nf(d.fleet_size)} color={C.slate}
                    hint="Machines in the fleet, bench/test units excluded." />
                <Kpi label="Earning now" value={latest ? nf(latest.avg_machines_earning) : '—'} color={C.primary}
                    hint="Machines earning on an average day in the last complete month (rented days ÷ days in month)." />
                <Kpi label={next ? `Forecast ${monthLabel(next.month)}` : 'Forecast'}
                    value={next ? nf(next.avg_machines_earning) : '—'} color={C.violet}
                    hint={next ? `Range ${nf(next.low)}–${nf(next.high)} machines. ${d.forecast.method}.` : 'Not enough history yet.'} />
                <Kpi label="Forecast error" value={d.forecast?.accuracy_mape_pct != null ? pctText(d.forecast.accuracy_mape_pct) : '—'}
                    color={C.warning} hint="Mean absolute percentage error when the same method is back-tested month by month. Lower is better." />
                <Kpi label="Spare capacity" value={next?.gap_vs_fleet != null ? nf(next.gap_vs_fleet) : '—'} color={C.info}
                    hint="Machines expected to be free next month — what the sales team can still sell." />
            </Stack>

            <Panel title="Demand — actual and forecast"
                subtitle={`Machines earning on an average day. ${d.forecast?.method || ''}`}>
                <ChartBox height={320}>
                    {demandChart ? (
                        <Bar data={demandChart} options={baseChartOptions('Machines', {
                            plugins: { legend: { labels: { filter: (l) => !!l.text } } },
                            scales: { y: { beginAtZero: true, grid: { color: C.grid }, title: { display: true, text: 'Machines earning' } } },
                        })} />
                    ) : <EmptyState message="No rental history yet" hint="Demand is built from contract lines — add rentals and it fills in." />}
                </ChartBox>
            </Panel>

            <Panel title="Dynamic pricing — what utilization argues for"
                subtitle={`Median contracted rate against utilization over the last ${d.pricing?.window_days || 90} days. Suggestions only — nothing is applied.`}
                action={<ExportButton onClick={() => downloadCsv('pricing_suggestions.csv',
                    ['Category', 'Machines', 'Utilization %', 'Median rate', 'Suggested rate', 'Change %', 'Stance', 'Rationale'],
                    (d.pricing?.rows || []).map(r => [r.category, r.machines, r.utilization_pct, r.median_monthly_rate,
                        r.suggested_monthly_rate, r.change_pct, r.stance, r.rationale]))} />}>
                <DataTable
                    columns={[
                        { key: 'category', label: 'Category' },
                        { key: 'machines', label: 'Machines', align: 'right', render: (r) => nf(r.machines) },
                        { key: 'util', label: 'Utilization', align: 'right', render: (r) => pctText(r.utilization_pct) },
                        { key: 'rate', label: 'Median rate', align: 'right', render: (r) => inr(r.median_monthly_rate) },
                        { key: 'suggested', label: 'Suggested', align: 'right', render: (r) => (
                            <Box component="span" sx={{ fontWeight: 800,
                                color: r.change_pct > 0 ? C.success : r.change_pct < 0 ? C.error : 'text.primary' }}>
                                {inr(r.suggested_monthly_rate)}
                                {r.change_pct ? ` (${r.change_pct > 0 ? '+' : ''}${r.change_pct}%)` : ''}
                            </Box>
                        ) },
                        { key: 'stance', label: 'Stance', render: (r) => (
                            <Chip size="small" label={r.stance} sx={{ fontWeight: 700,
                                bgcolor: r.change_pct > 0 ? `${C.success}1a` : r.change_pct < 0 ? `${C.error}1a` : `${C.slate}1a`,
                                color: r.change_pct > 0 ? C.success : r.change_pct < 0 ? C.error : C.slate }} />
                        ) },
                        { key: 'rationale', label: 'Why' },
                    ]}
                    rows={d.pricing?.rows || []}
                    empty="No categories with rental history in this window"
                />
            </Panel>

            <Panel title="MEWP market intel"
                subtitle="Headlines the Fleet Agent collects from public sources — stored here, never mailed">
                {intelChart && (
                    <ChartBox height={180}>
                        <Bar data={intelChart} options={baseChartOptions('Items', { plugins: { legend: { display: false } } })} />
                    </ChartBox>
                )}
                <DataTable
                    columns={[
                        { key: 'published', label: 'Published', render: (r) => (r.published_at
                            ? new Date(r.published_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—') },
                        { key: 'source', label: 'Source' },
                        { key: 'title', label: 'Headline', render: (r) => (
                            <Box component="a" href={r.url} target="_blank" rel="noopener noreferrer"
                                sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 600,
                                    display: 'inline-flex', alignItems: 'center', gap: .5,
                                    '&:hover': { textDecoration: 'underline' } }}>
                                {r.title}<OpenInNewIcon sx={{ fontSize: 13 }} />
                            </Box>
                        ) },
                        { key: 'keywords', label: 'Matched on' },
                    ]}
                    rows={items}
                    empty="No market items collected yet"
                />
            </Panel>

            {d.sources?.note && (
                <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: '12px' }}>
                    {d.sources.note}
                </Alert>
            )}
        </Stack>
    );
};

const ExportButton = ({ onClick }) => (
    <Button size="small" variant="outlined" color="inherit" startIcon={<DownloadIcon />} onClick={onClick}
        sx={{ borderColor: 'rgba(148,163,184,.4)', color: 'text.secondary', fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
        CSV
    </Button>
);

const LoadingPanels = () => (
    <Stack spacing={2}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {[...Array(4)].map((_, i) => <Skeleton key={i} variant="rounded" height={92} sx={{ flex: '1 1 190px', borderRadius: '14px' }} />)}
        </Stack>
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: '16px' }} />
        <Skeleton variant="rounded" height={280} sx={{ borderRadius: '16px' }} />
    </Stack>
);

// ── 1. UTILIZATION ─────────────────────────────────────────────────────────
const UtilizationView = ({ d, loading, range }) => {
    const monthly = useMemo(() => {
        if (!d?.monthly?.length) return null;
        return {
            labels: d.monthly.map(m => monthLabel(m.month)),
            datasets: [
                { type: 'bar', label: 'Rental utilization %', data: d.monthly.map(m => m.rental_utilization_pct), backgroundColor: C.primary, borderRadius: 6, yAxisID: 'y', order: 2 },
                { type: 'line', label: 'Working hours', data: d.monthly.map(m => m.working_hours), borderColor: C.warning, backgroundColor: C.warning, tension: .35, yAxisID: 'y1', order: 1, pointRadius: 3 },
            ],
        };
    }, [d]);

    const top = useMemo(() => {
        if (!d?.machines?.length) return null;
        const rows = d.machines.slice(0, 12);
        return {
            labels: rows.map(m => m.serial_number || m.equipment_id),
            datasets: [{ label: 'Rental utilization %', data: rows.map(m => m.rental_utilization_pct), backgroundColor: C.primary, borderRadius: 6 }],
        };
    }, [d]);

    if (loading && !d) return <LoadingPanels />;
    if (!d) return null;
    const f = d.fleet;

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Kpi label="Rental utilization" value={pctText(f.rental_utilization_pct)} color={C.primary}
                    hint="Days on rent ÷ (machines × days in range). How much of the fleet is earning." />
                <Kpi label="Operating utilization" value={pctText(f.operating_utilization_pct)} color={C.warning}
                    hint={`Engine hours ÷ (days on rent × ${d.notes?.shift_hours || 8}h shift). How hard the hired iron actually works.`} />
                <Kpi label="Rented days" value={nf(f.rented_days)} color={C.info}
                    hint={`Out of ${nf(f.possible_rental_days)} possible machine-days.`} />
                <Kpi label="Working hours" value={nf(f.working_hours)} color={C.success}
                    hint="Engine-meter hours accumulated across the fleet in the range." />
                <Kpi label="Fleet" value={`${nf(f.machines)}`} color={C.slate}
                    hint={`${nf(f.tracked_machines)} with a telematics unit — untracked machines count for rental utilization only.`} />
            </Stack>

            <Panel title="Utilization trend" subtitle="Rental utilization % (bars) against engine hours worked (line), per month">
                <ChartBox height={300}>
                    {monthly ? (
                        <Bar data={monthly} options={baseChartOptions('Utilization %', {
                            scales: {
                                y: { beginAtZero: true, max: 100, grid: { color: C.grid }, title: { display: true, text: 'Utilization %' } },
                                y1: { beginAtZero: true, position: 'right', grid: { display: false }, title: { display: true, text: 'Engine hours' } },
                            },
                        })} />
                    ) : <EmptyState />}
                </ChartBox>
            </Panel>

            <Panel title="Utilization by machine" subtitle="Top 12 by share of the period spent on rent">
                <ChartBox height={280}>
                    {top ? <Bar data={top} options={baseChartOptions('Utilization %', { plugins: { legend: { display: false } } })} /> : <EmptyState />}
                </ChartBox>
            </Panel>

            <Panel title="Every machine" subtitle="Rented days, engine hours and both utilization rates"
                action={<ExportButton onClick={() => downloadCsv(`utilization_${range.from}_${range.to}.csv`,
                    ['Serial', 'Equipment', 'Brand', 'Model', 'Tracked', 'Rented days', 'Run days', 'Idle on rent', 'Working hours', 'Rental util %', 'Operating util %'],
                    d.machines.map(m => [m.serial_number, m.equipment_id, m.brand, m.model, m.tracked ? 'Yes' : 'No', m.rented_days, m.run_days, m.idle_on_rent_days, m.working_hours, m.rental_utilization_pct, m.operating_utilization_pct]))} />}>
                <DataTable
                    rows={d.machines}
                    empty="No machines in the fleet"
                    columns={[
                        { key: 'machine', label: 'Machine', render: machineCell },
                        { key: 'rented_days', label: 'Rented days', align: 'right', render: (r) => nf(r.rented_days) },
                        { key: 'run_days', label: 'Ran on', align: 'right', render: (r) => (r.tracked ? `${nf(r.run_days)} days` : '—') },
                        { key: 'working_hours', label: 'Engine hrs', align: 'right', render: (r) => (r.tracked ? nf(r.working_hours) : '—') },
                        { key: 'rental_utilization_pct', label: 'Rental utilization', render: (r) => <PctBar value={r.rental_utilization_pct} color={C.primary} /> },
                        {
                            key: 'operating_utilization_pct', label: 'Operating utilization',
                            render: (r) => (r.tracked
                                ? <PctBar value={r.operating_utilization_pct} color={C.warning} />
                                : <Chip size="small" label="No telematics" sx={{ fontWeight: 700, borderRadius: '8px' }} />),
                        },
                    ]}
                />
            </Panel>
        </Stack>
    );
};

// ── 2. IDLE TIME ───────────────────────────────────────────────────────────
const IdleView = ({ d, loading, range }) => {
    const trend = useMemo(() => {
        if (!d?.trend?.length) return null;
        // Long ranges get noisy at day resolution — thin the labels, keep the data.
        return {
            labels: d.trend.map(t => dayLabel(t.date)),
            datasets: [
                { label: 'On rent', data: d.trend.map(t => t.on_rent), borderColor: C.primary, backgroundColor: 'rgba(37,99,235,.12)', fill: true, tension: .3, pointRadius: 0 },
                { label: 'Actually ran', data: d.trend.map(t => t.ran), borderColor: C.success, backgroundColor: 'rgba(16,185,129,.15)', fill: true, tension: .3, pointRadius: 0 },
            ],
        };
    }, [d]);

    if (loading && !d) return <LoadingPanels />;
    if (!d) return null;
    const f = d.fleet;

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Kpi label="Idle rate" value={pctText(f.idle_pct)} color={C.warning}
                    hint="Machine-days on rent where the engine never turned, as a share of judged days." />
                <Kpi label="Idle days" value={nf(f.idle_days)} color={C.error} hint="Paid-for days with no work done." />
                <Kpi label="Worked days" value={nf(f.worked_days)} color={C.success} />
                <Kpi label="Avg hrs / rented day" value={f.avg_hours_per_rented_day ?? '—'} color={C.primary}
                    hint="Engine hours ÷ machine-days on rent." />
                <Kpi label="On rent, untracked" value={nf(f.untracked_on_rent)} color={C.slate}
                    hint="On rent without a telematics unit — no idle verdict is possible for these." />
            </Stack>

            <Panel title="On rent vs actually running" subtitle="Machines on rent each day, and how many turned the engine">
                <ChartBox height={300}>
                    {trend ? <Line data={trend} options={baseChartOptions('Machines', {
                        scales: { x: { grid: { display: false }, ticks: { maxTicksLimit: 14, font: { size: 10 } } } },
                    })} /> : <EmptyState />}
                </ChartBox>
            </Panel>

            <Panel title="Idle machines" subtitle="Sorted by idle days — the top of this list is where money leaks"
                action={<ExportButton onClick={() => downloadCsv(`idle_time_${range.from}_${range.to}.csv`,
                    ['Serial', 'Equipment', 'Customer', 'Rented days', 'Worked days', 'Idle days', 'No-data days', 'Engine hrs', 'Idle %'],
                    d.machines.map(m => [m.serial_number, m.equipment_id, m.customer, m.rented_days, m.worked_days, m.idle_days, m.no_data_days, m.working_hours, m.idle_pct]))} />}>
                <DataTable
                    rows={d.machines}
                    empty="No machine was on rent in this period"
                    columns={[
                        { key: 'machine', label: 'Machine', render: machineCell },
                        { key: 'customer', label: 'Customer', render: (r) => r.customer || '—' },
                        { key: 'rented_days', label: 'Rented', align: 'right', render: (r) => nf(r.rented_days) },
                        { key: 'worked_days', label: 'Worked', align: 'right', render: (r) => nf(r.worked_days) },
                        { key: 'idle_days', label: 'Idle', align: 'right', render: (r) => <Typography variant="body2" fontWeight={800} color={r.idle_days > 0 ? 'warning.dark' : 'text.secondary'}>{nf(r.idle_days)}</Typography> },
                        { key: 'no_data_days', label: 'No data', align: 'right', render: (r) => nf(r.no_data_days) },
                        { key: 'working_hours', label: 'Engine hrs', align: 'right', render: (r) => nf(r.working_hours) },
                        { key: 'idle_pct', label: 'Idle share', render: (r) => <PctBar value={r.idle_pct} color={C.warning} /> },
                    ]}
                />
            </Panel>
        </Stack>
    );
};

// ── 3. OFF-RENT LATENCY ────────────────────────────────────────────────────
const LatencyView = ({ d, loading, range }) => {
    const monthly = useMemo(() => {
        if (!d?.monthly?.length) return null;
        return {
            labels: d.monthly.map(m => monthLabel(m.month)),
            datasets: [
                { type: 'bar', label: 'Avg days to re-rent', data: d.monthly.map(m => m.avg_latency_days), backgroundColor: C.violet, borderRadius: 6, yAxisID: 'y' },
                { type: 'line', label: 'Re-rentals', data: d.monthly.map(m => m.re_rentals), borderColor: C.info, backgroundColor: C.info, tension: .35, yAxisID: 'y1', pointRadius: 3 },
            ],
        };
    }, [d]);

    if (loading && !d) return <LoadingPanels />;
    if (!d) return null;
    const s = d.summary;

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Kpi label="Avg days to re-rent" value={s.avg_latency_days ?? '—'} color={C.violet}
                    hint="Days between a de-hire and the machine's next rental start, for re-rentals inside the range." />
                <Kpi label="Median" value={s.median_latency_days ?? '—'} color={C.primary} />
                <Kpi label="Re-rented within 7 days" value={pctText(s.within_7_days_pct)} color={C.success} />
                <Kpi label="Waiting right now" value={nf(s.machines_waiting_now)} color={C.warning}
                    hint="Machines currently de-hired / available / waiting for rent." />
                <Kpi label="Avg wait so far" value={s.avg_waiting_days_now != null ? `${s.avg_waiting_days_now} d` : '—'} color={C.error}
                    hint="How long the machines waiting now have already been off rent." />
            </Stack>

            <Panel title="Re-hire speed by month" subtitle="Average days off rent before the next job started">
                <ChartBox height={300}>
                    {monthly ? <Bar data={monthly} options={baseChartOptions('Days', {
                        scales: {
                            y: { beginAtZero: true, grid: { color: C.grid }, title: { display: true, text: 'Days' } },
                            y1: { beginAtZero: true, position: 'right', grid: { display: false }, title: { display: true, text: 'Re-rentals' } },
                        },
                    })} /> : <EmptyState message="No re-rentals in this period" hint="A re-rental needs a de-hire followed by a new contract." />}
                </ChartBox>
            </Panel>

            <Panel title="Waiting for a job now" subtitle="Off-rent machines, longest wait first"
                action={<ExportButton onClick={() => downloadCsv(`off_rent_waiting_${range.to}.csv`,
                    ['Serial', 'Equipment', 'Brand', 'Model', 'Status', 'Since', 'Days waiting'],
                    d.waiting.map(w => [w.serial_number, w.equipment_id, w.brand, w.model, w.status, w.since, w.waiting_days]))} />}>
                <DataTable
                    rows={d.waiting}
                    empty="Every machine is on rent, in service, or unaccounted for"
                    maxHeight={320}
                    columns={[
                        { key: 'machine', label: 'Machine', render: machineCell },
                        { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status ? statusLabel(r.status) : '—'} sx={{ fontWeight: 700, borderRadius: '8px' }} /> },
                        { key: 'since', label: 'Off rent since', render: (r) => r.since || '—' },
                        {
                            key: 'waiting_days', label: 'Days waiting', align: 'right',
                            render: (r) => <Typography variant="body2" fontWeight={800} color={(r.waiting_days || 0) > 30 ? 'error.main' : 'text.primary'}>{nf(r.waiting_days)}</Typography>,
                        },
                    ]}
                />
            </Panel>

            <Panel title="Completed re-rentals" subtitle="Each de-hire → next rental gap counted in the range"
                action={<ExportButton onClick={() => downloadCsv(`off_rent_latency_${range.from}_${range.to}.csv`,
                    ['Serial', 'Equipment', 'Off rent on', 'Back on rent', 'Latency days', 'Source'],
                    d.gaps.map(g => [g.serial_number, g.equipment_id, g.off_ymd, g.on_ymd, g.latency_days, g.source]))} />}>
                <DataTable
                    rows={d.gaps}
                    empty="No completed re-rentals in this period"
                    columns={[
                        { key: 'machine', label: 'Machine', render: machineCell },
                        { key: 'off_ymd', label: 'Off rent' },
                        { key: 'on_ymd', label: 'Back on rent' },
                        { key: 'latency_days', label: 'Days idle', align: 'right', render: (r) => <Typography variant="body2" fontWeight={800}>{nf(r.latency_days)}</Typography> },
                        { key: 'source', label: 'From', render: (r) => <Typography variant="caption" color="text.disabled">{r.source === 'status_log' ? 'status audit' : 'contract dates'}</Typography> },
                    ]}
                />
            </Panel>
        </Stack>
    );
};

// ── 4. MAINTENANCE COMPLIANCE ──────────────────────────────────────────────
const MaintenanceView = ({ d, loading, range }) => {
    const donut = useMemo(() => {
        if (!d?.plan) return null;
        const p = d.plan;
        const vals = [p.compliant, p.overdue, p.no_plan];
        if (!vals.some(v => v > 0)) return null;
        return {
            labels: ['On plan', 'Overdue', 'No plan date'],
            datasets: [{ data: vals, backgroundColor: [C.success, C.error, C.slate], borderWidth: 0, hoverOffset: 14 }],
        };
    }, [d]);

    if (loading && !d) return <LoadingPanels />;
    if (!d) return null;
    const p = d.plan, t = d.tickets;

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Kpi label="PM compliance" value={pctText(p.compliance_pct)} color={C.teal}
                    hint="Machines whose next service date has not passed, out of those with a planned date." />
                <Kpi label="Overdue machines" value={nf(p.overdue)} color={C.error} />
                <Kpi label="Avg days overdue" value={p.avg_days_overdue ?? '—'} color={C.warning} />
                <Kpi label="PM tickets on time" value={pctText(t.on_time_pct)} color={C.success}
                    hint="PM / inspection tickets closed on or before their target date." />
                <Kpi label="Open past due" value={nf(t.open_overdue)} color={C.error}
                    hint="PM tickets still open with the target date behind us." />
            </Stack>

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
                <Panel title="Plan adherence" subtitle="Against the PM schedule (pm_schedule)" sx={{ flex: '0 0 auto', width: { xs: '100%', lg: 360 } }}>
                    <ChartBox height={260}>
                        {donut ? <Doughnut data={donut} options={{
                            maintainAspectRatio: false, cutout: '68%',
                            plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, padding: 14, font: { weight: '600' } } } },
                        }} /> : <EmptyState message="No PM plan rows for this fleet" hint="pm_schedule is keyed by serial number." />}
                    </ChartBox>
                </Panel>

                <Panel title="Service tickets in range" subtitle="Preventive maintenance and inspection tickets" sx={{ flex: 1 }}>
                    <Stack spacing={1.25} sx={{ py: 1 }}>
                        {[
                            ['Raised', nf(t.raised)],
                            ['Closed', nf(t.closed)],
                            ['Closed on time', nf(t.closed_on_time)],
                            ['Still open past due', nf(t.open_overdue)],
                            ['Avg days to close', t.avg_close_days ?? '—'],
                        ].map(([k, v]) => (
                            <React.Fragment key={k}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary" fontWeight={600}>{k}</Typography>
                                    <Typography variant="body2" fontWeight={800}>{v}</Typography>
                                </Stack>
                                <Divider />
                            </React.Fragment>
                        ))}
                        {!d.sources?.service_tickets && (
                            <Typography variant="caption" color="text.disabled">
                                Service ticket data unavailable — ticket figures are blank.
                            </Typography>
                        )}
                    </Stack>
                </Panel>
            </Stack>

            <Panel title="Machines by PM state" subtitle="Overdue first — days overdue counted against the planned service date"
                action={<ExportButton onClick={() => downloadCsv(`maintenance_compliance_${range.to}.csv`,
                    ['Serial', 'Equipment', 'Brand', 'Model', 'PM id', 'Last service', 'Next plan', 'Next due', 'Days overdue', 'State'],
                    d.machines.map(m => [m.serial_number, m.equipment_id, m.brand, m.model, m.pm_id, m.last_service_date, m.next_service_plan, m.next_due_date, m.days_overdue, m.state]))} />}>
                <DataTable
                    rows={d.machines}
                    empty="No PM schedule rows matched this fleet"
                    columns={[
                        { key: 'machine', label: 'Machine', render: machineCell },
                        { key: 'last_service_date', label: 'Last service', render: (r) => r.last_service_date || '—' },
                        { key: 'next_service_plan', label: 'Next job', render: (r) => r.next_service_plan || '—' },
                        { key: 'next_due_date', label: 'Due', render: (r) => r.next_due_date || '—' },
                        { key: 'days_overdue', label: 'Days overdue', align: 'right', render: (r) => (r.days_overdue ? <Typography variant="body2" fontWeight={800} color="error.main">{nf(r.days_overdue)}</Typography> : '—') },
                        {
                            key: 'state', label: 'State',
                            render: (r) => {
                                const map = { ON_PLAN: ['On plan', C.success], OVERDUE: ['Overdue', C.error], NO_PLAN: ['No plan date', C.slate] };
                                const [label, color] = map[r.state] || ['—', C.slate];
                                return <Chip size="small" label={label} sx={{ fontWeight: 700, borderRadius: '8px', bgcolor: `${color}1a`, color }} />;
                            },
                        },
                    ]}
                />
            </Panel>
        </Stack>
    );
};

// ── 5. QUOTE → ORDER ───────────────────────────────────────────────────────
const ConversionView = ({ d, loading, range }) => {
    const monthly = useMemo(() => {
        if (!d?.monthly?.length) return null;
        return {
            labels: d.monthly.map(m => monthLabel(m.month)),
            datasets: [
                { type: 'bar', label: 'Won', data: d.monthly.map(m => m.won), backgroundColor: C.success, borderRadius: 6, stack: 's', yAxisID: 'y' },
                { type: 'bar', label: 'Lost', data: d.monthly.map(m => m.lost), backgroundColor: C.error, borderRadius: 6, stack: 's', yAxisID: 'y' },
                { type: 'bar', label: 'Open', data: d.monthly.map(m => m.quoted), backgroundColor: C.slate, borderRadius: 6, stack: 's', yAxisID: 'y' },
                { type: 'line', label: 'Conversion %', data: d.monthly.map(m => m.conversion_pct), borderColor: C.primary, backgroundColor: C.primary, tension: .35, yAxisID: 'y1', pointRadius: 3 },
            ],
        };
    }, [d]);

    if (loading && !d) return <LoadingPanels />;
    if (!d) return null;
    const s = d.summary;

    return (
        <Stack spacing={2}>
            <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: '12px' }}>
                {d.sources?.note}
            </Alert>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Kpi label="Conversion" value={pctText(s.conversion_pct)} color={C.info}
                    hint="Won ÷ (won + lost). Open quotes are excluded until they are decided." />
                <Kpi label="Opportunities" value={nf(s.opportunities)} color={C.primary} />
                <Kpi label="Won" value={nf(s.won)} color={C.success} />
                <Kpi label="Lost" value={nf(s.lost)} color={C.error} />
                <Kpi label="Still open" value={nf(s.quoted_open)} color={C.slate} />
                <Kpi label="Machines placed" value={nf(s.machines_won)} color={C.violet}
                    hint="Machine lines across the contracts that were won." />
            </Stack>

            <Panel title="Pipeline by month" subtitle="Won / lost / open per month with the conversion rate">
                <ChartBox height={300}>
                    {monthly ? <Bar data={monthly} options={baseChartOptions('Contracts', {
                        scales: {
                            x: { stacked: true, grid: { display: false } },
                            y: { stacked: true, beginAtZero: true, grid: { color: C.grid }, title: { display: true, text: 'Contracts' } },
                            y1: { beginAtZero: true, max: 100, position: 'right', grid: { display: false }, title: { display: true, text: 'Conversion %' } },
                        },
                    })} /> : <EmptyState />}
                </ChartBox>
            </Panel>

            <Panel title="By customer" subtitle="Who converts, and who keeps quoting"
                action={<ExportButton onClick={() => downloadCsv(`conversion_customers_${range.from}_${range.to}.csv`,
                    ['Customer', 'Won', 'Lost', 'Open', 'Machines', 'Conversion %'],
                    d.customers.map(c => [c.customer, c.won, c.lost, c.quoted, c.machines, c.conversion_pct]))} />}>
                <DataTable
                    rows={d.customers}
                    empty="No contracts raised in this period"
                    maxHeight={320}
                    columns={[
                        { key: 'customer', label: 'Customer', render: (r) => <Typography variant="body2" fontWeight={700}>{r.customer}</Typography> },
                        { key: 'won', label: 'Won', align: 'right', render: (r) => nf(r.won) },
                        { key: 'lost', label: 'Lost', align: 'right', render: (r) => nf(r.lost) },
                        { key: 'quoted', label: 'Open', align: 'right', render: (r) => nf(r.quoted) },
                        { key: 'machines', label: 'Machines', align: 'right', render: (r) => nf(r.machines) },
                        { key: 'conversion_pct', label: 'Conversion', render: (r) => <PctBar value={r.conversion_pct} color={C.info} /> },
                    ]}
                />
            </Panel>

            <Panel title="Opportunities" subtitle="Every contract raised in the range and where it landed"
                action={<ExportButton onClick={() => downloadCsv(`conversion_${range.from}_${range.to}.csv`,
                    ['Contract', 'Customer', 'Site', 'Type', 'Machines', 'Raised', 'Status', 'Stage'],
                    d.rows.map(r => [r.rental_contract_id, r.customer, r.site_location, r.rental_type, r.machines, r.raised_on, r.status, r.stage]))} />}>
                <DataTable
                    rows={d.rows}
                    empty="No contracts raised in this period"
                    columns={[
                        { key: 'rental_contract_id', label: 'Contract', render: (r) => <Typography variant="body2" fontWeight={800} color="primary.main">{r.rental_contract_id}</Typography> },
                        { key: 'customer', label: 'Customer', render: (r) => r.customer || '—' },
                        { key: 'site_location', label: 'Site', render: (r) => r.site_location || '—' },
                        { key: 'machines', label: 'Machines', align: 'right', render: (r) => nf(r.machines) },
                        { key: 'raised_on', label: 'Raised', render: (r) => r.raised_on || '—' },
                        {
                            key: 'stage', label: 'Outcome',
                            render: (r) => {
                                const map = { won: ['Won', C.success], lost: ['Lost', C.error], quoted: ['Open', C.slate] };
                                const [label, color] = map[r.stage] || ['—', C.slate];
                                return <Chip size="small" label={`${label} · ${r.status || ''}`.trim()} sx={{ fontWeight: 700, borderRadius: '8px', bgcolor: `${color}1a`, color }} />;
                            },
                        },
                    ]}
                />
            </Panel>
        </Stack>
    );
};

// ── 6. REVENUE PER ASSET ───────────────────────────────────────────────────
const RevenueView = ({ d, loading, range }) => {
    const top = useMemo(() => {
        if (!d?.machines?.length) return null;
        const rows = d.machines.slice(0, 12);
        return {
            labels: rows.map(m => m.serial_number || m.equipment_id),
            datasets: [
                { label: 'Accrued', data: rows.map(m => m.accrued_revenue), backgroundColor: C.success, borderRadius: 6 },
                { label: 'Billed (GST invoices)', data: rows.map(m => m.billed_revenue), backgroundColor: C.primary, borderRadius: 6 },
            ],
        };
    }, [d]);

    if (loading && !d) return <LoadingPanels />;
    if (!d) return null;
    const s = d.summary;

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Kpi label="Accrued revenue" value={inrShort(s.accrued_revenue)} color={C.success}
                    hint="Contract rate × days on rent (monthly ÷ 30, or hourly × engine hours). Independent of whether it has been billed." />
                <Kpi label="Billed (GST)" value={inrShort(s.billed_revenue)} color={C.primary}
                    hint="Invoice line items matched to a machine by serial number." />
                <Kpi label="Monthly invoices" value={inrShort(s.monthly_invoiced_revenue)} color={C.info}
                    hint="Auto-generated monthly invoices. Kept separate from GST bills — they often cover the same rental." />
                <Kpi label="Revenue per asset" value={inrShort(s.avg_revenue_per_asset)} color={C.violet}
                    hint="Accrued revenue ÷ every machine in the fleet, earning or not." />
                <Kpi label="Earning nothing" value={nf(s.zero_revenue_machines)} color={C.error}
                    hint="Machines with no rental revenue at all in this window." />
            </Stack>

            <Panel title="Top earners" subtitle="Accrued against billed, top 12 machines">
                <ChartBox height={300}>
                    {top ? <Bar data={top} options={baseChartOptions('₹', {
                        plugins: { tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${inr(ctx.parsed.y)}` } } },
                    })} /> : <EmptyState />}
                </ChartBox>
            </Panel>

            <Panel title="Revenue by machine" subtitle="Accrued revenue, and what each rented day and engine hour earned"
                action={<ExportButton onClick={() => downloadCsv(`revenue_per_asset_${range.from}_${range.to}.csv`,
                    ['Serial', 'Equipment', 'Brand', 'Model', 'Customers', 'Rented days', 'Engine hrs', 'Accrued', 'Billed (GST)', 'Monthly invoiced', '₹/rented day', '₹/hour'],
                    d.machines.map(m => [m.serial_number, m.equipment_id, m.brand, m.model, (m.customers || []).join(' | '), m.rented_days, m.working_hours, m.accrued_revenue, m.billed_revenue, m.monthly_invoiced_revenue, m.revenue_per_rented_day, m.revenue_per_hour]))} />}>
                <DataTable
                    rows={d.machines}
                    empty="No machines in the fleet"
                    columns={[
                        { key: 'machine', label: 'Machine', render: machineCell },
                        { key: 'customers', label: 'Customer', render: (r) => (r.customers?.length ? r.customers.join(', ') : '—') },
                        { key: 'rented_days', label: 'Rented days', align: 'right', render: (r) => nf(r.rented_days) },
                        { key: 'accrued_revenue', label: 'Accrued', align: 'right', render: (r) => <Typography variant="body2" fontWeight={800}>{inr(r.accrued_revenue)}</Typography> },
                        { key: 'billed_revenue', label: 'Billed', align: 'right', render: (r) => inr(r.billed_revenue) },
                        { key: 'revenue_per_rented_day', label: '₹ / rented day', align: 'right', render: (r) => inr(r.revenue_per_rented_day) },
                        { key: 'revenue_per_hour', label: '₹ / engine hr', align: 'right', render: (r) => inr(r.revenue_per_hour) },
                    ]}
                />
            </Panel>
        </Stack>
    );
};

// ── 7. CUSTOMER PROFITABILITY ──────────────────────────────────────────────
const CustomersView = ({ d, loading, range }) => {
    const chart = useMemo(() => {
        if (!d?.customers?.length) return null;
        const rows = d.customers.slice(0, 10);
        return {
            labels: rows.map(c => (c.customer.length > 22 ? c.customer.slice(0, 21) + '…' : c.customer)),
            datasets: [
                { type: 'bar', label: 'Accrued revenue', data: rows.map(c => c.accrued_revenue), backgroundColor: C.success, borderRadius: 6, yAxisID: 'y' },
                { type: 'line', label: 'Service tickets', data: rows.map(c => c.tickets), borderColor: C.error, backgroundColor: C.error, tension: .3, yAxisID: 'y1', pointRadius: 4 },
            ],
        };
    }, [d]);

    if (loading && !d) return <LoadingPanels />;
    if (!d) return null;
    const s = d.summary;

    return (
        <Stack spacing={2}>
            <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: '12px' }}>
                {d.sources?.note}
            </Alert>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Kpi label="Customers on rent" value={nf(s.customers)} color={C.primary} />
                <Kpi label="Accrued revenue" value={inrShort(s.accrued_revenue)} color={C.success} />
                <Kpi label="Top customer share" value={pctText(s.top_customer_share_pct)} color={C.warning}
                    hint={s.top_customer ? `${s.top_customer} — concentration risk if this climbs.` : undefined} />
                <Kpi label="Avg per customer" value={inrShort(s.avg_revenue_per_customer)} color={C.violet} />
                <Kpi label="Service tickets" value={nf(s.total_tickets)} color={C.error}
                    hint="Tickets raised on machines while that customer held them." />
            </Stack>

            <Panel title="Revenue against service burden" subtitle="Top 10 customers by accrued revenue, with the tickets their fleet generated">
                <ChartBox height={300}>
                    {chart ? <Bar data={chart} options={baseChartOptions('₹', {
                        plugins: { tooltip: { callbacks: { label: (ctx) => (ctx.dataset.yAxisID === 'y' ? `${ctx.dataset.label}: ${inr(ctx.parsed.y)}` : `${ctx.dataset.label}: ${ctx.parsed.y}`) } } },
                        scales: {
                            y: { beginAtZero: true, grid: { color: C.grid }, title: { display: true, text: 'Revenue ₹' } },
                            y1: { beginAtZero: true, position: 'right', grid: { display: false }, title: { display: true, text: 'Tickets' } },
                        },
                    })} /> : <EmptyState />}
                </ChartBox>
            </Panel>

            <Panel title="Customer ledger" subtitle="Revenue, how hard they run the machines, and what it costs us in service"
                action={<ExportButton onClick={() => downloadCsv(`customer_profitability_${range.from}_${range.to}.csv`,
                    ['Customer', 'Machines', 'Contracts', 'Rented days', 'Engine hrs', 'Accrued', 'Billed', '₹/machine', '₹/rented day', 'Tickets', 'Breakdowns', 'Labour hrs', 'Usage intensity %'],
                    d.customers.map(c => [c.customer, c.machines, c.contracts, c.rented_days, c.working_hours, c.accrued_revenue, c.invoiced_revenue, c.revenue_per_machine, c.revenue_per_rented_day, c.tickets, c.breakdown_tickets, c.labour_hours, c.usage_intensity_pct]))} />}>
                <DataTable
                    rows={d.customers}
                    empty="No customer had a machine on rent in this period"
                    columns={[
                        { key: 'customer', label: 'Customer', render: (r) => (
                            <Box>
                                <Typography variant="body2" fontWeight={700}>{r.customer}</Typography>
                                <Typography variant="caption" color="text.disabled">{plural(r.machines, 'machine')} · {plural(r.contracts, 'contract')} · {plural(r.sites, 'site')}</Typography>
                            </Box>
                        ) },
                        { key: 'rented_days', label: 'Rented days', align: 'right', render: (r) => nf(r.rented_days) },
                        { key: 'accrued_revenue', label: 'Accrued', align: 'right', render: (r) => <Typography variant="body2" fontWeight={800}>{inr(r.accrued_revenue)}</Typography> },
                        { key: 'revenue_per_machine', label: '₹ / machine', align: 'right', render: (r) => inr(r.revenue_per_machine) },
                        { key: 'tickets', label: 'Tickets', align: 'right', render: (r) => (
                            <Typography variant="body2" fontWeight={700} color={r.breakdown_tickets > 0 ? 'error.main' : 'text.primary'}>
                                {nf(r.tickets)}{r.breakdown_tickets ? ` (${r.breakdown_tickets} bd)` : ''}
                            </Typography>
                        ) },
                        { key: 'labour_hours', label: 'Labour hrs', align: 'right', render: (r) => nf(r.labour_hours) },
                        { key: 'usage_intensity_pct', label: 'Usage intensity', render: (r) => <PctBar value={r.usage_intensity_pct} color={C.warning} /> },
                    ]}
                />
            </Panel>
        </Stack>
    );
};

/* ── FINANCE OVERVIEW ────────────────────────────────────────────────────────
   The finance team's page: the telematics-installed fleet reduced to the
   numbers finance acts on. Headline = accrued vs billed (the gap is the
   story), then the ACTION QUEUE — every rupee that is staged but not yet
   billed or recovered: extra hours the Fleet Agent measured above contracted
   shifts (O1), leakage where a contract ended but the machine is still out
   (R7), idle installed machines, and machines whose hour meter cannot be
   trusted (telemetry silent — figures withheld, never invoiced stale).
   Everything is STAGED for a human decision; nothing posts to the ledger. */
const ACTION_META = {
    EXTRA_HOURS: { label: 'Extra hours',   color: C.success, chip: 'Billable — review & raise' },
    LEAKAGE:     { label: 'Leakage',       color: C.error,   chip: 'Unbilled days running' },
    IDLE:        { label: 'Idle machine',  color: C.warning, chip: 'Revenue at risk' },
    UNTRUSTED:   { label: 'Meter unsafe',  color: C.slate,   chip: 'Fix before billing hours' },
};

const FinanceView = ({ d, loading, range }) => {
    const monthlyChart = useMemo(() => {
        const rows = d?.monthly || [];
        if (!rows.length) return null;
        return {
            labels: rows.map(r => monthLabel(r.month)),
            datasets: [
                { label: 'Accrued (contract rate × days on rent)', data: rows.map(r => r.accrued),
                  backgroundColor: C.primary, borderRadius: 6 },
                { label: 'Billed (monthly invoices)', data: rows.map(r => r.billed),
                  backgroundColor: C.success, borderRadius: 6 },
            ],
        };
    }, [d]);

    if (loading && !d) return <LoadingPanels />;
    if (!d) return null;

    const s = d.summary || {};
    const actions = d.actions || [];
    const flagged = (d.machines || []).filter(m => m.flags?.untrusted || m.flags?.idle || m.flags?.leakage);

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                <Kpi label="Accrued revenue" value={inrShort(s.accrued_revenue)} color={C.primary} loading={loading}
                    hint="Contract rate × days on rent inside the window, telematics-installed machines only." />
                <Kpi label="Billed" value={inrShort(s.billed_revenue)} color={C.success} loading={loading}
                    hint="Monthly invoices plus GST invoice lines matched to a machine by serial number." />
                <Kpi label="Billing gap" value={inrShort(s.billing_gap)}
                    color={(s.billing_gap || 0) > 0 ? C.error : C.success} loading={loading}
                    hint="Accrued minus billed. Positive = work done that has not been invoiced yet." />
                <Kpi label="Extra hours staged" value={s.extra_hours_staged_value != null ? inrShort(s.extra_hours_staged_value) : '—'}
                    color={C.violet} loading={loading}
                    hint="Hours the Fleet Agent measured above the contracted shift, valued at each contract's extra-hours rate. Draft for review — never auto-posted." />
                <Kpi label="Leakage" value={s.leakage_value != null ? inrShort(s.leakage_value) : '—'} color={C.error} loading={loading}
                    hint="Contracts past their end date with the machine still out — every day is unbilled (Fleet Agent rule R7)." />
                <Kpi label="Utilization" value={pctText(s.rental_utilization_pct)} color={C.info} loading={loading}
                    hint="Rented days ÷ (machines × days in window) across the installed fleet." />
                <Kpi label="Idle machines" value={nf(s.idle_machines)} color={C.warning} loading={loading}
                    hint="Installed machines with zero rented days in the window — iron earning nothing." />
                <Kpi label="Meters not trusted" value={nf(s.untrusted_machines)} color={C.slate} loading={loading}
                    hint="Telemetry silent for over 24 h — hour figures for these machines are withheld, not reported stale." />
            </Stack>

            <Panel title="Accrued vs billed, month by month"
                subtitle="The two should track each other — a widening gap is unbilled work or missing invoice matching.">
                <ChartBox height={300}>
                    {monthlyChart ? (
                        <Bar data={monthlyChart} options={baseChartOptions('₹', {
                            scales: { y: { beginAtZero: true, grid: { color: C.grid },
                                ticks: { callback: (v) => inrShort(v) } } },
                        })} />
                    ) : <EmptyState message="No revenue in this window"
                            hint="Accrued revenue comes from contract lines; billed from the invoice tables." />}
                </ChartBox>
            </Panel>

            <Panel title="Finance action queue"
                subtitle="Every rupee that needs a decision, largest first. Staged only — nothing here has touched the ledger."
                action={<ExportButton onClick={() => downloadCsv(`finance_actions_${range.from}_${range.to}.csv`,
                    ['Type', 'Machine', 'Amount', 'Detail', 'Action'],
                    actions.map(a => [a.type, a.equipment_id, a.amount, a.detail, a.action]))} />}>
                <DataTable
                    empty="Nothing waiting on finance — no staged overruns, leakage, or idle machines in this window"
                    rows={actions}
                    columns={[
                        { key: 'type', label: 'Type', render: (r) => {
                            const m = ACTION_META[r.type] || { label: r.type, color: C.slate, chip: '' };
                            return <Chip size="small" label={m.label}
                                sx={{ fontWeight: 700, color: '#fff', bgcolor: m.color }} />;
                        } },
                        { key: 'equipment_id', label: 'Machine', render: (r) => (
                            <Typography variant="body2" fontWeight={700}>{r.equipment_id}</Typography>) },
                        { key: 'amount', label: 'Amount', align: 'right', render: (r) => (
                            <Typography variant="body2" fontWeight={800}
                                color={r.amount != null ? 'text.primary' : 'text.disabled'}>
                                {r.amount != null ? inr(r.amount) : 'needs rate'}
                            </Typography>) },
                        { key: 'detail', label: 'What happened', render: (r) => r.detail },
                        { key: 'action', label: 'Suggested action', render: (r) => (
                            <Typography variant="caption" color="text.secondary">{r.action}</Typography>) },
                    ]}
                />
            </Panel>

            <Panel title="Per-machine economics (telematics-installed fleet)"
                subtitle="Hour figures for silent trackers are withheld — a stale meter is never a billing basis."
                action={<ExportButton onClick={() => downloadCsv(`finance_machines_${range.from}_${range.to}.csv`,
                    ['Machine', 'Serial', 'Model', 'Customer', 'Rented days', 'Idle days', 'Engine hrs',
                     'Accrued', 'Billed', 'Gap', '₹/engine hr', 'Extra hrs staged', 'Flags'],
                    (d.machines || []).map(m => [m.equipment_id, m.serial_number, m.model, m.customer,
                        m.rented_days, m.idle_days, m.engine_hours, m.accrued_revenue, m.billed_revenue,
                        m.billing_gap, m.revenue_per_engine_hour, m.extra_hours_staged,
                        Object.entries(m.flags || {}).filter(([, v]) => v).map(([k]) => k).join(' ')]))} />}>
                <DataTable
                    empty="No telematics-installed machines found"
                    rows={d.machines || []}
                    columns={[
                        { key: 'equipment_id', label: 'Machine', render: (r) => (
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <Typography variant="body2" fontWeight={700}>{r.serial_number || r.equipment_id}</Typography>
                                {(r.flags?.untrusted || r.flags?.leakage) &&
                                    <Tooltip title={r.flags.untrusted ? 'Telemetry silent — hour figures withheld' : 'Contract ended, machine still out'}>
                                        <FlagIcon sx={{ fontSize: 15, color: r.flags.leakage ? C.error : C.slate }} />
                                    </Tooltip>}
                            </Stack>) },
                        { key: 'model', label: 'Model', render: (r) => r.model || '—' },
                        { key: 'customer', label: 'Customer', render: (r) => r.customer || '—' },
                        { key: 'rented_days', label: 'Rented', align: 'right', render: (r) => nf(r.rented_days) },
                        { key: 'engine_hours', label: 'Engine hrs', align: 'right', render: (r) => (
                            r.engine_hours == null
                                ? <Typography variant="body2" color="text.disabled">withheld</Typography>
                                : nf(r.engine_hours)) },
                        { key: 'accrued_revenue', label: 'Accrued', align: 'right', render: (r) => (
                            <Typography variant="body2" fontWeight={800}>{inr(r.accrued_revenue)}</Typography>) },
                        { key: 'billed_revenue', label: 'Billed', align: 'right', render: (r) => inr(r.billed_revenue) },
                        { key: 'billing_gap', label: 'Gap', align: 'right', render: (r) => (
                            <Typography variant="body2" fontWeight={700}
                                color={(r.billing_gap || 0) > 0 ? 'error.main' : 'text.secondary'}>
                                {inr(r.billing_gap)}
                            </Typography>) },
                        { key: 'revenue_per_engine_hour', label: '₹ / engine hr', align: 'right',
                          render: (r) => inr(r.revenue_per_engine_hour) },
                        { key: 'extra_hours_staged', label: 'Extra hrs', align: 'right', render: (r) => (
                            r.extra_hours_staged
                                ? <Typography variant="body2" fontWeight={700} color={C.violet}>
                                      {nf(r.extra_hours_staged)}{r.extra_hours_value != null ? ` (${inr(r.extra_hours_value)})` : ''}
                                  </Typography>
                                : '—') },
                    ]}
                />
            </Panel>

            {d.sources?.note && (
                <Typography variant="caption" color="text.disabled" sx={{ px: 0.5 }}>
                    {d.sources.note}
                </Typography>
            )}
        </Stack>
    );
};

export default AnalysisPage;
